import { useEffect, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/hooks/useAuth';
import { 
  fetchFriends, 
  fetchFriendRequests, 
  searchUsers,
  browseUsers,
  sendFriendRequest, 
  respondToFriendRequest, 
  unfriend,
  updateFriendshipTier,
  requestHigherTier
} from '../services/friendsApi';
import { 
  FriendSummary, 
  FriendRequest, 
  FriendSearchResult, 
  FriendshipTier 
} from '../types/friends';
import { FRIEND_REQUEST_COUNT_REFRESH_EVENT } from './useFriendRequestCount';

export function useFriends(timeWindowKey: string = '6m') {
  const { session, sessionEstablishing } = useAuth();
  const queryClient = useQueryClient();
  
  const [searchResults, setSearchResults] = useState<FriendSearchResult[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);

  // Phase 3: Use React Query for friends list with SWR
  const { 
    data: friends = [], 
    isLoading: friendsLoading,
    error: friendsError
  } = useQuery<FriendSummary[]>({
    queryKey: ['friends', timeWindowKey],
    queryFn: () => fetchFriends(session!.access_token, timeWindowKey),
    enabled: !!session?.access_token && !sessionEstablishing,
    staleTime: 120000, // 2 minutes (matches backend cache TTL)
    gcTime: 300000, // 5 minutes (keep in cache)
    retry: 2,
    // SWR: Keep previous data while refetching
    placeholderData: (previousData) => previousData,
  });

  // Phase 3: Use React Query for friend requests with SWR
  const { 
    data: requestsData,
    isLoading: requestsLoading,
    error: requestsError
  } = useQuery<{ incoming: FriendRequest[]; outgoing: FriendRequest[] }>({
    queryKey: ['friendRequests'],
    queryFn: () => fetchFriendRequests(session!.access_token),
    enabled: !!session?.access_token && !sessionEstablishing,
    staleTime: 60000, // 1 minute
    gcTime: 300000, // 5 minutes
    retry: 2,
    placeholderData: (previousData) => previousData,
  });

  const incomingRequests = requestsData?.incoming ?? [];
  const outgoingRequests = requestsData?.outgoing ?? [];
  const loading = friendsLoading || requestsLoading;
  const error = friendsError || requestsError ? 'Failed to load friends data' : null;

  // Refresh function that invalidates both queries
  const loadData = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['friends', timeWindowKey] }),
      queryClient.invalidateQueries({ queryKey: ['friendRequests'] }),
    ]);
  }, [queryClient, timeWindowKey]);

  // Handle search
  useEffect(() => {
    if (!session?.access_token || sessionEstablishing) return;
    
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const controller = new AbortController();
    setSearchLoading(true);

    const runSearch = async () => {
      try {
        const res = await searchUsers(session.access_token, searchQuery);
        if (!controller.signal.aborted) {
          setSearchResults(res);
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        if (!controller.signal.aborted) {
          setSearchLoading(false);
        }
      }
    };

    // Debounce search
    const timeoutId = setTimeout(runSearch, 300);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [searchQuery, session?.access_token, sessionEstablishing]);

  // Load browse results
  const loadBrowseResults = useCallback(async () => {
    if (!session?.access_token || sessionEstablishing) return;
    
    setBrowseLoading(true);
    try {
      const res = await browseUsers(session.access_token, 15);
      setSearchResults(res);
    } catch (error) {
      console.error('Browse failed:', error);
    } finally {
      setBrowseLoading(false);
    }
  }, [session?.access_token, sessionEstablishing]);

  // Actions - invalidate queries after mutations
  const handleSendRequest = async (toUserId: string, tier: FriendshipTier, note?: string) => {
    if (!session?.access_token) return;
    await sendFriendRequest(session.access_token, { toUserId, tier, note });
    // Invalidate queries to refetch
    await loadData();
    // Also update search results to show pending status
    setSearchResults(prev => prev.map(r => 
      r.userId === toUserId ? { ...r, hasOutgoingRequest: true } : r
    ));
  };

  const handleRespondToRequest = async (
    requestId: string, 
    action: 'accept' | 'accept_lower' | 'reject' | 'request_higher',
    tier?: FriendshipTier,
    note?: string
  ) => {
    if (!session?.access_token) return;
    // @ts-ignore - TS struggles with the discriminated union in the function signature vs implementation
    await respondToFriendRequest(session.access_token, requestId, { action, tier, note });
    // Invalidate queries to refetch
    await loadData();
    
    // Dispatch custom event to refresh notification count immediately
    window.dispatchEvent(new CustomEvent(FRIEND_REQUEST_COUNT_REFRESH_EVENT));
  };

  const handleUnfriend = async (friendId: string) => {
    if (!session?.access_token) return;
    await unfriend(session.access_token, friendId);
    // Invalidate queries to refetch
    await loadData();
  };

  const handleUpdateTier = async (friendId: string, tier: FriendshipTier) => {
    if (!session?.access_token) return;
    await updateFriendshipTier(session.access_token, friendId, tier);
    // Invalidate queries to refetch
    await loadData();
  };

  const handleRequestHigherTier = async (friendId: string, tier: FriendshipTier, note?: string) => {
    if (!session?.access_token) return;
    await requestHigherTier(session.access_token, friendId, tier, note);
    // Invalidate queries to refetch
    await loadData();
  };

  return {
    friends,
    incomingRequests,
    outgoingRequests,
    searchResults,
    searchQuery,
    setSearchQuery,
    loading,
    searchLoading,
    browseLoading,
    error,
    loadBrowseResults,
    refresh: loadData,
    sendRequest: handleSendRequest,
    respondToRequest: handleRespondToRequest,
    unfriend: handleUnfriend,
    updateTier: handleUpdateTier,
    requestHigherTier: handleRequestHigherTier
  };
}
